/**
 * SSH Collector API Service
 *
 * Provides CRUD operations for SSH Collector configurations and child records.
 */

import api from "./api";

const sp = (schemaName) => (schemaName ? { client: schemaName } : {});

// =============================================================================
// SSHCollectorConfig CRUD
// =============================================================================

export const getAllSSHCollectorConfigs = async (schemaName = null) => {
  try {
    const response = await api.get("/ssh-collector-configs/", { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error fetching SSH collector configs:", error);
    throw error;
  }
};

export const getSSHCollectorConfigsWithPagination = async (params = {}) => {
  try {
    const response = await api.get("/ssh-collector-configs/", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching SSH collector configs:", error);
    throw error;
  }
};

export const getSSHCollectorConfigById = async (id, schemaName = null) => {
  try {
    const response = await api.get(`/ssh-collector-configs/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error fetching SSH collector config:", error);
    throw error;
  }
};

export const createSSHCollectorConfig = async (data, schemaName = null) => {
  try {
    const response = await api.post("/ssh-collector-configs/", data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error creating SSH collector config:", error);
    throw error;
  }
};

export const updateSSHCollectorConfig = async (id, data, schemaName = null) => {
  try {
    const response = await api.put(`/ssh-collector-configs/${id}/`, data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error updating SSH collector config:", error);
    throw error;
  }
};

export const deleteSSHCollectorConfig = async (id, schemaName = null) => {
  try {
    const response = await api.delete(`/ssh-collector-configs/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error deleting SSH collector config:", error);
    throw error;
  }
};

export const approveSSHCollectorConfig = async (id, schemaName = null) => {
  try {
    const response = await api.post(`/ssh-collector-configs/${id}/approve/`, {}, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error approving SSH collector config:", error);
    throw error;
  }
};

export const denySSHCollectorConfig = async (id, schemaName = null) => {
  try {
    const response = await api.post(`/ssh-collector-configs/${id}/deny/`, {}, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error denying SSH collector config:", error);
    throw error;
  }
};

// =============================================================================
// SSHCollectorTarget CRUD
// =============================================================================

export const getSSHCollectorTargetsByConfig = async (configId, schemaName = null) => {
  try {
    const response = await api.get(`/ssh-collector-targets/`, { params: { ssh_collector_config: configId, ...sp(schemaName) } });
    return response.data;
  } catch (error) {
    console.error("Error fetching SSH collector targets:", error);
    throw error;
  }
};

export const getSSHCollectorTargetById = async (id, schemaName = null) => {
  try {
    const response = await api.get(`/ssh-collector-targets/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error fetching SSH collector target:", error);
    throw error;
  }
};

export const createSSHCollectorTarget = async (data, schemaName = null) => {
  try {
    const response = await api.post("/ssh-collector-targets/", data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error creating SSH collector target:", error);
    throw error;
  }
};

export const updateSSHCollectorTarget = async (id, data, schemaName = null) => {
  try {
    const response = await api.put(`/ssh-collector-targets/${id}/`, data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error updating SSH collector target:", error);
    throw error;
  }
};

export const deleteSSHCollectorTarget = async (id, schemaName = null) => {
  try {
    const response = await api.delete(`/ssh-collector-targets/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error deleting SSH collector target:", error);
    throw error;
  }
};

// =============================================================================
// SSHCollectorMedicrossSite CRUD
// =============================================================================

export const getSSHCollectorMedicrossByConfig = async (configId, schemaName = null) => {
  try {
    const response = await api.get(`/ssh-collector-medicross/`, { params: { ssh_collector_config: configId, ...sp(schemaName) } });
    return response.data;
  } catch (error) {
    console.error("Error fetching SSH collector Medicross sites:", error);
    throw error;
  }
};

export const getSSHCollectorMedicrossById = async (id, schemaName = null) => {
  try {
    const response = await api.get(`/ssh-collector-medicross/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error fetching SSH collector Medicross site:", error);
    throw error;
  }
};

export const createSSHCollectorMedicross = async (data, schemaName = null) => {
  try {
    const response = await api.post("/ssh-collector-medicross/", data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error creating SSH collector Medicross site:", error);
    throw error;
  }
};

export const updateSSHCollectorMedicross = async (id, data, schemaName = null) => {
  try {
    const response = await api.put(`/ssh-collector-medicross/${id}/`, data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error updating SSH collector Medicross site:", error);
    throw error;
  }
};

export const deleteSSHCollectorMedicross = async (id, schemaName = null) => {
  try {
    const response = await api.delete(`/ssh-collector-medicross/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error deleting SSH collector Medicross site:", error);
    throw error;
  }
};

// =============================================================================
// Bulk Operations
// =============================================================================

export const bulkDeleteSSHCollectorConfigs = async (ids, schemaName = null) => {
  try {
    const deletePromises = ids.map(id => deleteSSHCollectorConfig(id, schemaName));
    return await Promise.all(deletePromises);
  } catch (error) {
    console.error("Error bulk deleting SSH collector configs:", error);
    throw error;
  }
};

export default {
  getAllSSHCollectorConfigs,
  getSSHCollectorConfigsWithPagination,
  getSSHCollectorConfigById,
  createSSHCollectorConfig,
  updateSSHCollectorConfig,
  deleteSSHCollectorConfig,
  approveSSHCollectorConfig,
  denySSHCollectorConfig,
  bulkDeleteSSHCollectorConfigs,
  getSSHCollectorTargetsByConfig,
  getSSHCollectorTargetById,
  createSSHCollectorTarget,
  updateSSHCollectorTarget,
  deleteSSHCollectorTarget,
  getSSHCollectorMedicrossByConfig,
  getSSHCollectorMedicrossById,
  createSSHCollectorMedicross,
  updateSSHCollectorMedicross,
  deleteSSHCollectorMedicross,
};
